import { ApiError } from './api/client.js';

/**
 * Utilidades para tratar errores de la API en la interfaz.
 *
 * Existe un archivo con esto y no una función suelta en cada pantalla porque
 * decidir QUÉ se le enseña al usuario ante un fallo se hace en tres sitios y
 * tiene que hacerse igual en los tres. Si cada pantalla decide por su cuenta,
 * aparecen tres comportamientos distintos para el mismo error y nadie sabe cuál
 * es el bueno.
 */

/**
 * Mensaje listo para pintar de cualquier error.
 *
 * @param {unknown} error
 * @returns {string}
 */
export function mensajeDe(error) {
  if (error instanceof ApiError) {
    // `status 0` es "no se pudo contactar con el servidor". Se distingue del
    // resto porque reintentar tiene sentido y culpar a la cuenta no.
    if (error.status === 0) {
      return 'No se pudo contactar con el servidor. Comprueba tu conexión y vuelve a intentarlo.';
    }
    return error.message;
  }

  // Un error que no es de la API es un bug del frontend. No se le enseña al
  // usuario un stack trace, pero sí se deja en la consola.
  console.error('Error no previsto en la interfaz', error);
  return 'Ha ocurrido un error inesperado.';
}

/**
 * Errores por campo, listas para React Hook Form.
 *
 * El backend devuelve `fieldErrors` con el MISMO nombre de campo que el
 * formulario (porque ambos salen del mismo schema Zod compartido). Por eso esto
 * es un `pick` y nada más: si algún día dejan de coincidir, el error simplemente
 * no se muestra en su campo, y se ve al enviar el formulario.
 *
 * @param {unknown} error
 * @returns {Record<string, string>}
 */
export function camposDe(error) {
  if (!(error instanceof ApiError)) return {};

  /** @type {Record<string, string>} */
  const campos = {};
  for (const [campo, mensajes] of Object.entries(error.fields)) {
    if (mensajes.length > 0) campos[campo] = mensajes[0];
  }
  return campos;
}

/**
 * ¿Este error vale la pena reintentar?
 *
 * Un 4xx significa que la petición está mal (o no hay permisos) y repetirla
 * exactamente igual va a fallar igual. Un 5xx o un fallo de red sí.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export function esReintentable(error) {
  return error instanceof ApiError && (error.status === 0 || error.status >= 500);
}
